import React, { useState, useEffect } from 'react';
import {
  BooleanField,
  useRecordContext,
  useUpdate,
  useNotify,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Stack,
  Typography,
  Paper,
  CircularProgress,
  TextField,
  Box,
} from '@mui/material';
import { DialogProps } from '@mui/material/Dialog';
import { Funeral } from '../types';
import { PaperworkCompleteNotificationDialog } from './PaperworkCompleteNotificationDialog';
import { benefitsSettled } from '../funerals/caseClosure';
import { usePaperworkNotification } from '../funerals/PaperworkNotificationContext';
import { StatusCell } from './StatusCell';

import { apiFetchResponse } from '../lib/apiFetch';

interface DecStatusFieldProps {
  source: string;
  label?: string;
  /** Render the compact shadcn StatusCell trigger (enriched board). */
  compact?: boolean;
}

interface ValidationData {
  readyToSubmit: boolean;
  missingRequiredCount: number;
  missingOptionalCount: number;
}

export const DecStatusField = ({
  source,
  label,
  compact,
}: DecStatusFieldProps) => {
  const record = useRecordContext<Funeral>();
  const [open, setOpen] = useState(false);
  const [update] = useUpdate();
  const notify = useNotify();
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [decReady, setDecReady] = useState<boolean>(false);
  const [deathCertificateNumber, setDeathCertificateNumber] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [paperworkDoneOpen, setPaperworkDoneOpen] = useState(false);
  const [funeralForNotification, setFuneralForNotification] = useState<Funeral | null>(null);
  // On the board, this row unmounts when the case moves to the collapsed
  // "Closed" group, so trigger the shared dialog hosted above the groups.
  // Falls back to the row-local dialog when there's no provider (show page).
  const paperworkNotification = usePaperworkNotification();

  useEffect(() => {
    if (record?.id) {
      void fetchValidationData();
    }
  }, [record?.id]);

  const fetchValidationData = async () => {
    try {
      setLoading(true);
      const response = await apiFetchResponse(`validation/summary/${record.id}`);

      if (!response.ok) {
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.success && data.data?.decForm) {
        setValidationData(data.data.decForm);
      }
    } catch (err) {
      console.error('Error fetching validation data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!record) return null;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Snapshot persisted values into local form state when the dialog opens.
    // Doing this here (instead of in a useEffect on `record`) prevents a
    // background record refetch from clobbering an in-progress edit.
    setDecReady(!!(record as any)[source]);
    setDeathCertificateNumber(record.death_certificate_number || '');
    setOpen(true);
  };

  const handleDialogClose: DialogProps['onClose'] = () => {
    setOpen(false);
  };

  const handleButtonClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
  };

  const isQuebec = ((record.region as string) || '').toLowerCase() === 'quebec';

  const handleSave = async () => {
    setSaving(true);
    const wasComplete = !!(record as any)[source];

    try {
      const updatedData = {
        ...record,
        [source]: decReady,
        death_certificate_number: deathCertificateNumber || null,
      };

      await update(
        'funerals',
        {
          id: record.id,
          data: updatedData,
          previousData: record,
        },
        {
          mutationMode: 'pessimistic',
          onSuccess: (data: any) => {
            notify('Status updated', { type: 'success' });
            setOpen(false);
            // Trigger the paperwork-done flow on either ordering — DEC may be
            // marked complete after benefits, or vice versa. Read the just-
            // persisted state from the update response (falling back to the
            // optimistic merge of record + form state) so the check is
            // immune to stale-closure issues on `record`.
            const merged = (data as Funeral) || ({
              ...(record as Funeral),
              [source]: decReady,
              death_certificate_number: deathCertificateNumber || null,
            } as Funeral);
            const decNowComplete = !!(merged as any)[source];
            // `benefitsSettled`, not `=== COMPLETED`: benefits also settle as
            // 'ineligible' or once the CPP packet is mailed, and those cases
            // used to close silently without ever prompting the email.
            const benefitsNowComplete = benefitsSettled(merged);
            const decTransitioned = !wasComplete && decNowComplete;
            if (isQuebec && decTransitioned && benefitsNowComplete) {
              if (paperworkNotification) {
                paperworkNotification.notifyPaperworkComplete(merged);
              } else {
                setFuneralForNotification(merged);
                setPaperworkDoneOpen(true);
              }
            }
          },
          onError: () => {
            notify('Error updating status', { type: 'error' });
          },
        },
      );
    } catch (error) {
      notify('Error updating status', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const getBorderColor = () => {
    if ((record as any)[source]) {
      return 'transparent';
    }
    if (loading) {
      return 'grey.400';
    }
    if (validationData?.readyToSubmit) {
      return 'success.main';
    }
    return 'error.main';
  };

  const getBackgroundColor = () => {
    if ((record as any)[source]) {
      return 'success.light';
    }
    return 'grey.100';
  };

  const getHoverColor = () => {
    if ((record as any)[source]) {
      return 'success.main';
    }
    return 'grey.200';
  };

  const getTextColor = () => {
    if ((record as any)[source]) {
      return 'white';
    }
    return 'text.primary';
  };

  if (loading) {
    if (compact) return <StatusCell variant="na" loading title={label} />;
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'inline-flex',
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: 'grey.100',
        }}
      >
        <CircularProgress size={16} />
      </Paper>
    );
  }

  return (
    <>
      {compact ? (
        <StatusCell
          variant={
            (record as any)[source]
              ? 'done'
              : validationData?.readyToSubmit
              ? 'ready'
              : 'missing'
          }
          title={label}
          onClick={(e) => {
            e.stopPropagation();
            setDecReady(!!(record as any)[source]);
            setDeathCertificateNumber(record.death_certificate_number || '');
            setOpen(true);
          }}
        />
      ) : (
        <Paper
          onClick={handleClick}
          elevation={0}
          sx={{
            display: 'inline-flex',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            backgroundColor: getBackgroundColor(),
            border: '2px solid',
            borderColor: getBorderColor(),
            '&:hover': {
              backgroundColor: getHoverColor(),
              transform: 'translateY(-1px)',
              boxShadow: 1,
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          <BooleanField
            source={source}
            label={label}
            sx={{
              '& .MuiTypography-root': {
                color: getTextColor(),
              },
            }}
          />
        </Paper>
      )}
      <div onClick={stopPropagation}>
        <Dialog
          open={open}
          onClose={handleDialogClose}
          onClick={stopPropagation}
          BackdropProps={{
            onClick: stopPropagation,
          }}
          PaperProps={{
            onClick: stopPropagation,
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{label || source}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ minWidth: 300, my: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>DEC complete</Typography>
                <Switch
                  checked={decReady}
                  onChange={(e) => setDecReady(e.target.checked)}
                  color="primary"
                />
              </Box>

              <TextField
                fullWidth
                label="Death Certificate Number"
                value={deathCertificateNumber}
                onChange={(e) => setDeathCertificateNumber(e.target.value)}
                placeholder="Enter confirmation number"
                variant="outlined"
                size="small"
              />

              {validationData && (
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Validation Status:
                  </Typography>
                  <Typography variant="body2">
                    {validationData.readyToSubmit
                      ? '✓ Ready to submit'
                      : `✗ ${validationData.missingRequiredCount} required fields missing`}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleButtonClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        <PaperworkCompleteNotificationDialog
          open={paperworkDoneOpen}
          onClose={() => setPaperworkDoneOpen(false)}
          funeral={funeralForNotification}
        />
      </div>
    </>
  );
};
